/**
 * Signup popup engine. One self-contained file, no dependencies, merchant-agnostic.
 *
 * Everything store-specific (copy, quiz options, Klaviyo list, discount code,
 * images, colours, timing rules) arrives as a config object. The engine never
 * hardcodes a merchant. That is what lets the same file run two ways:
 *
 *   1. Theme mode (today). A Liquid snippet inlines the config as
 *      <script type="application/json" data-signup-popup> and loads this file
 *      from the theme's assets. Built by scripts/shopify/deploy-signup-popup.js.
 *   2. Hosted mode (later). <script src=".../signup-popup.js" data-config-url="...">
 *      and the engine fetches the config. Same file, no code change.
 *
 * Signups go straight from the browser to Klaviyo's public client API
 * (company_id = the store's public key, safe to expose). Styles live in a
 * shadow root so no theme CSS can reach in and no popup CSS can leak out.
 *
 * Debug URL params: ?sp=open (force open), ?sp=reset (clear stored state).
 */
(function () {
  "use strict";
  if (window.SignupPopup && window.SignupPopup.__loaded) return;

  var VERSION = "1.0.0";
  var CURRENT_SCRIPT = document.currentScript;
  var KLAVIYO_BASE = "https://a.klaviyo.com";
  var DEFAULT_REVISION = "2026-07-15";

  // ---------- small utils ----------
  function merge(a, b) {
    var out = {};
    var k;
    for (k in a) if (Object.prototype.hasOwnProperty.call(a, k)) out[k] = a[k];
    for (k in b) {
      if (!Object.prototype.hasOwnProperty.call(b, k)) continue;
      var av = out[k], bv = b[k];
      out[k] = av && bv && typeof av === "object" && typeof bv === "object" && !Array.isArray(bv) ? merge(av, bv) : bv;
    }
    return out;
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  // Copy may carry a small allowlist of inline markup (links in the SMS consent).
  // Everything is escaped, then <a href="https://..."> / <a href="/..."> is restored.
  function richText(s) {
    return esc(s).replace(/&lt;a href=&quot;((?:https:\/\/|\/)[^"&<>]*)&quot;&gt;(.*?)&lt;\/a&gt;/g, function (_, href, text) {
      return '<a href="' + href + '" target="_blank" rel="noopener">' + text + "</a>";
    });
  }
  function fill(tpl, vars) {
    return String(tpl || "").replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ""; });
  }
  var store = {
    get: function (k) { try { return JSON.parse(localStorage.getItem(k)); } catch (e) { return null; } },
    set: function (k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} },
    del: function (k) { try { localStorage.removeItem(k); } catch (e) {} },
    sget: function (k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    sset: function (k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };
  var DAY = 86400000;

  function toE164(raw, cc) {
    var s = String(raw || "").trim();
    if (s.charAt(0) === "+") {
      var p = "+" + s.replace(/\D/g, "");
      return /^\+\d{8,15}$/.test(p) ? p : null;
    }
    var d = s.replace(/\D/g, "");
    if (cc === "1") {
      if (d.length === 10) return "+1" + d;
      if (d.length === 11 && d.charAt(0) === "1") return "+" + d;
      return null;
    }
    return d.length >= 6 && d.length <= 14 ? "+" + cc + d : null;
  }
  function validEmail(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e); }

  // ---------- styles (inside the shadow root) ----------
  function css(t) {
    return [
      ":host{all:initial}",
      "*,*::before,*::after{box-sizing:border-box}",
      ".sp{--accent:" + t.accent + ";--accent-ink:" + t.accentText + ";--ink:" + t.ink + ";--muted:" + t.muted + ";--paper:" + t.paper + ";--line:" + t.line + ";--radius:" + t.radius + ";",
      "font-family:" + t.fontBody + ";color:var(--ink);-webkit-font-smoothing:antialiased}",
      "dialog.sp:focus{outline:none}",
      "dialog.sp{padding:0;border:0;background:transparent;max-width:none;max-height:none;width:100%;height:100%;margin:0;inset:0;overflow:hidden;display:none;align-items:center;justify-content:center}",
      "dialog.sp[open]{display:flex}",
      "dialog.sp::backdrop{background:" + t.backdrop + ";opacity:0;transition:opacity .3s ease}",
      "dialog.sp.is-in::backdrop{opacity:1}",
      ".sp-panel{position:relative;display:grid;grid-template-columns:44% 1fr;width:min(780px,calc(100vw - 32px));min-height:460px;background:var(--paper);border-radius:var(--radius);overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,.28);opacity:0;transform:translateY(14px) scale(.98);transition:opacity .35s ease,transform .45s cubic-bezier(.2,.8,.2,1)}",
      ".is-in .sp-panel{opacity:1;transform:none}",
      ".sp-media{position:relative;background:#e9e3d6;overflow:hidden}",
      ".sp-media img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scale(1.06);opacity:0;transition:transform 1.4s cubic-bezier(.2,.8,.2,1),opacity .6s ease}",
      ".is-in .sp-media img.is-loaded{transform:none;opacity:1}",
      ".sp-body{position:relative;display:flex;flex-direction:column;justify-content:center;padding:44px 40px 36px}",
      ".sp-close{position:absolute;top:10px;right:10px;z-index:3;width:36px;height:36px;border:0;border-radius:50%;background:rgba(255,255,255,.85);color:var(--ink);cursor:pointer;display:grid;place-items:center;transition:background .2s}",
      ".sp-close:hover{background:#fff}",
      ".sp-title:focus{outline:none}",
      "button:focus{outline:none}",
      "button:focus-visible,a:focus-visible{outline:2px solid var(--accent);outline-offset:2px}",
      ".sp-close svg{width:16px;height:16px}",
      ".sp-step{display:none}",
      ".sp-step.is-active{display:block;animation:sp-step-in .42s cubic-bezier(.2,.8,.2,1) both}",
      "@keyframes sp-step-in{from{opacity:0;transform:translateX(18px)}to{opacity:1;transform:none}}",
      ".sp-eyebrow{margin:0 0 10px;font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--accent)}",
      ".sp-title{margin:0 0 10px;font-family:" + t.fontHeading + ";font-weight:700;font-size:30px;line-height:1.12;color:var(--ink)}",
      ".sp-text{margin:0 0 20px;font-size:15px;line-height:1.5;color:var(--muted)}",
      ".sp-options{display:grid;gap:10px;margin:0 0 14px}",
      ".sp-opt{display:flex;align-items:center;gap:12px;width:100%;padding:13px 16px;border:1.5px solid var(--line);border-radius:12px;background:#fff;color:var(--ink);font:inherit;font-size:15px;font-weight:500;text-align:left;cursor:pointer;transition:border-color .18s,transform .18s,box-shadow .18s}",
      ".sp-opt:hover{border-color:var(--accent);transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,0,0,.06)}",
      ".sp-opt.is-picked{border-color:var(--accent);background:var(--accent);color:var(--accent-ink)}",
      ".sp-opt-dot{flex:none;width:18px;height:18px;border-radius:50%;border:1.5px solid var(--line);transition:all .18s}",
      ".sp-opt.is-picked .sp-opt-dot{border-color:var(--accent-ink);box-shadow:inset 0 0 0 4px var(--accent);background:var(--accent-ink)}",
      ".sp-form{display:grid;gap:10px}",
      ".sp-input{width:100%;padding:14px 16px;border:1.5px solid var(--line);border-radius:12px;background:#fff;color:var(--ink);font:inherit;font-size:16px;outline:none;transition:border-color .18s,box-shadow .18s}",
      ".sp-input:focus{border-color:var(--accent);box-shadow:0 0 0 3px color-mix(in srgb,var(--accent) 18%,transparent)}",
      ".sp-btn{width:100%;padding:15px 18px;border:0;border-radius:12px;background:var(--accent);color:var(--accent-ink);font:inherit;font-size:16px;font-weight:600;cursor:pointer;transition:filter .18s,transform .18s,background-color .6s ease}",
      ".sp-eyebrow,.sp-copy,.sp-check{transition:color .6s ease,background-color .6s ease}",
      ".sp-btn:hover{filter:brightness(1.08)}",
      ".sp-btn:active{transform:scale(.99)}",
      ".sp-btn[disabled]{opacity:.7;cursor:progress}",
      ".sp-link{display:block;margin:12px auto 0;padding:4px;border:0;background:none;color:var(--muted);font:inherit;font-size:13px;text-decoration:underline;text-underline-offset:3px;cursor:pointer}",
      ".sp-fine{margin:12px 0 0;font-size:11px;line-height:1.45;color:var(--muted)}",
      ".sp-fine a{color:inherit;text-decoration:underline;text-underline-offset:2px}",
      ".sp-consent{margin:2px 0 4px;font-size:11.5px}",
      ".sp-error{min-height:0;margin:0;font-size:13px;color:#a3321f}",
      ".sp-error:empty{display:none}",
      ".sp-hp{position:absolute;left:-9999px;width:1px;height:1px;opacity:0}",
      ".sp-code{display:flex;align-items:center;justify-content:space-between;gap:12px;margin:0 0 12px;padding:14px 16px;border:1.5px dashed var(--accent);border-radius:12px;background:#fff}",
      ".sp-code-val{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:22px;font-weight:700;letter-spacing:.06em;color:var(--ink)}",
      ".sp-copy{padding:8px 12px;border:0;border-radius:8px;background:color-mix(in srgb,var(--accent) 12%,#fff);color:var(--accent);font:inherit;font-size:13px;font-weight:600;cursor:pointer}",
      // offer badges on the success step
      ".sp-badges{margin:16px 0 0;padding:14px 0 0;border-top:1px solid var(--line)}",
      ".sp-badges-h{margin:0 0 10px;font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)}",
      ".sp-badge{display:flex;gap:12px;align-items:flex-start;margin:0 0 10px}",
      ".sp-badge:last-child{margin-bottom:0}",
      ".sp-badge-i{flex:none;display:grid;place-items:center;min-width:44px;height:28px;padding:0 8px;border-radius:999px;background:color-mix(in srgb,var(--accent) 12%,#fff);color:var(--accent);font-size:13px;font-weight:700;transition:color .6s ease,background-color .6s ease}",
      ".sp-badge-t{margin:0;font-size:13.5px;line-height:1.4;color:var(--ink)}",
      ".sp-badge-t b{font-weight:650}",
      ".sp-check{width:44px;height:44px;margin:0 0 14px;border-radius:50%;background:var(--accent);display:grid;place-items:center}",
      ".sp-check svg{width:22px;height:22px;stroke:var(--accent-ink);stroke-dasharray:30;stroke-dashoffset:30}",
      ".is-active .sp-check svg{animation:sp-draw .5s .15s ease forwards}",
      "@keyframes sp-draw{to{stroke-dashoffset:0}}",
      ".sp-teaser{position:fixed;left:16px;bottom:16px;z-index:2147482000;display:flex;align-items:center;gap:8px;padding:11px 14px 11px 16px;border:0;border-radius:999px;background:var(--accent);color:var(--accent-ink);font:inherit;font-size:14px;font-weight:600;box-shadow:0 8px 24px rgba(0,0,0,.2);cursor:pointer;transform:translateY(140%);transition:transform .45s cubic-bezier(.2,.8,.2,1)}",
      ".sp-teaser.is-shown{transform:none}",
      ".sp-teaser-x{display:grid;place-items:center;width:20px;height:20px;margin-left:2px;border-radius:50%;background:rgba(255,255,255,.18)}",
      ".sp-teaser-x svg{width:10px;height:10px}",
      "@media (max-width:749px){",
      "dialog.sp{align-items:flex-end}",
      ".sp-panel{grid-template-columns:1fr;grid-template-rows:auto 1fr;width:100%;min-height:0;max-height:92vh;overflow-y:auto;border-radius:var(--radius) var(--radius) 0 0;transform:translateY(100%);transition:opacity .2s ease,transform .45s cubic-bezier(.2,.8,.2,1)}",
      ".sp-media{height:" + t.mobileImageHeight + "}",
      ".sp-body{padding:24px 20px calc(22px + env(safe-area-inset-bottom))}",
      ".sp-title{font-size:25px}",
      ".sp-text{margin-bottom:16px;font-size:14.5px}",
      ".sp-opt{padding:12px 14px}",
      ".sp-teaser{left:12px;bottom:12px}",
      "}",
      // ---- full-screen layout: a scene per step (background + product layers) behind the card ----
      "dialog.sp--full{justify-content:flex-end}",
      "dialog.sp--full::backdrop{background:rgba(0,0,0,.25)}",
      ".sp-stage{position:absolute;inset:0;z-index:0;overflow:hidden;background:var(--scene," + t.ink + ");transition:background-color .7s ease;opacity:0}",
      ".is-in .sp-stage{opacity:1;transition:background-color .7s ease,opacity .45s ease}",
      // Leaving scene: fades out quickly where it is (the hand does not ride back down); its
      // pieces reset only once it is invisible. Arriving scene: starts as the old one clears.
      ".sp-scene{position:absolute;inset:0;opacity:0;visibility:hidden;transition:opacity .22s ease,visibility 0s linear .22s}",
      ".sp-scene.is-active{opacity:1;visibility:visible;transition:opacity .55s ease .18s}",
      ".sp-scene:not(.is-active) .sp-layer,.sp-scene:not(.is-active) .sp-bg-img,.sp-scene:not(.is-active) .sp-photo{transition-delay:.25s!important;transition-duration:0s!important}",
      ".sp-bg-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;transform:scale(1.04);transition:transform 6s cubic-bezier(.2,.8,.2,1)}",
      ".is-in .sp-scene.is-active .sp-bg-img{transform:scale(1)}",
      // Drifting garnish: a seamless tile repeated across a strip one tile wider than the
      // screen, slid left by exactly one tile per loop, so the loop point is invisible.
      ".sp-drift{--ts:var(--ts0);position:absolute;top:0;left:0;height:100%;width:calc(100% + var(--ts));background-repeat:repeat;background-size:var(--ts) var(--ts);animation:sp-drift var(--dur) linear infinite;will-change:transform}",
      "@keyframes sp-drift{from{transform:translate3d(0,0,0)}to{transform:translate3d(calc(-1 * var(--ts)),0,0)}}",
      ".sp-layers{position:absolute;left:0;top:0;bottom:0;right:calc(" + t.cardWidth + " + 8vw);transform-origin:50% 100%;transition:transform 1s cubic-bezier(.2,.8,.2,1),height 1s cubic-bezier(.2,.8,.2,1)}",
      ".sp-layer{position:absolute;left:var(--x);bottom:var(--b);height:var(--h);z-index:var(--z,1);translate:var(--tx,0) var(--ty,0);opacity:0;transform:translate(var(--ex,0),var(--ey,70px)) rotate(calc(var(--r,0deg) * 3));transition:transform .9s cubic-bezier(.2,.8,.2,1),opacity .35s ease}",
      ".is-in .sp-scene.is-active .sp-layer{opacity:1;transform:translateY(0) rotate(var(--r,0deg));transition:transform 1s cubic-bezier(.2,.8,.2,1) var(--d,0s),opacity .6s ease var(--d,0s)}",
      // top-pinned layers (layer.t): measured from the top of the product area, so they stay put
      // while the phone's product area shrinks when the drawer rises
      ".sp-layer--top{bottom:auto;top:var(--t)}",
      ".sp-layer img{display:block;height:100%;width:auto;filter:drop-shadow(0 22px 26px rgba(0,0,0,.38));animation:sp-float 7s ease-in-out var(--f,0s) infinite alternate}",
      "@keyframes sp-float{from{transform:translateY(0)}to{transform:translateY(-10px)}}",
      // A scene photo (a real shot with its own background) fills the product area and fades
      // into the table on the card side so the two surfaces meet without a hard edge.
      ".sp-photo{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:var(--pos,50% 50%);-webkit-mask-image:linear-gradient(90deg,#000 78%,transparent);mask-image:linear-gradient(90deg,#000 78%,transparent);opacity:0;transform:scale(1.05);transition:opacity .5s ease,transform 1.4s cubic-bezier(.2,.8,.2,1)}",
      ".is-in .sp-scene.is-active .sp-photo{opacity:1;transform:none}",
      // Flat-lay scenes (product shot from above, lying on the table): pieces are set down onto
      // the surface (a slight drop in scale) instead of rising in, sit still, and cast a short shadow.
      ".sp-scene--flat .sp-layer{transform:scale(1.14) rotate(var(--r,0deg));transition:transform .7s cubic-bezier(.2,.8,.2,1),opacity .3s ease}",
      ".is-in .sp-scene--flat.is-active .sp-layer{transform:scale(1) rotate(var(--r,0deg));transition:transform .8s cubic-bezier(.2,.8,.2,1) var(--d,0s),opacity .45s ease var(--d,0s)}",
      ".sp-scene--photo .sp-layer img{animation:none;filter:none}",
      // rise: a hand-held shot comes up from the bottom edge of its area and settles
      ".sp-scene--rise .sp-layer{transform:translateY(105%);opacity:1;transition:transform .5s ease}",
      ".is-in .sp-scene--rise.is-active .sp-layer{transform:none;transition:transform .65s cubic-bezier(.16,.84,.24,1) calc(var(--d,0s) + .18s)}",
      ".sp-scene--rise .sp-layer img{animation:none;filter:none}",
      // still: pieces are simply there when the scene fades in (no per-piece motion, no settle)
      // still scenes: every piece flies in from its own direction and they all land together
      ".sp-scene--still .sp-layer{opacity:0;transform:translate(var(--fx,0),var(--fy,0)) rotate(calc(var(--r,0deg) * 2.5));transition:none}",
      ".is-in .sp-scene--still.is-active .sp-layer{opacity:1;transform:rotate(var(--r,0deg));transition:transform .7s cubic-bezier(.2,.8,.2,1) .2s,opacity .45s ease .2s}",
      ".sp-scene--still .sp-bg-img,.is-in .sp-scene--still.is-active .sp-bg-img{transform:none;transition:none}",
      ".sp-scene--still .sp-layer img{animation:none;filter:drop-shadow(0 6px 8px rgba(0,0,0,.35)) drop-shadow(0 1px 2px rgba(0,0,0,.25))}",
      // photo-style scenes fade in only (no slide, no scale)
      ".sp-scene--photo .sp-layer,.is-in .sp-scene--photo.is-active .sp-layer{transform:none}",
      ".is-in .sp-scene--photo.is-active .sp-layer{transition:opacity .8s ease var(--d,0s)}",
      ".sp-scene--flat .sp-layer img{animation:none;filter:drop-shadow(0 5px 6px rgba(0,0,0,.38)) drop-shadow(0 1px 1px rgba(0,0,0,.3))}",
      // Full-screen choreography: .is-in fades the scene and brings the product in, then
      // .is-drawer (drawerDelayMs later) brings the card. The shopper sees the product first.
      ".sp--full .sp-panel{z-index:1;display:block;grid-template-columns:none;width:" + t.cardWidth + ";min-height:0;max-height:calc(100vh - 48px);overflow-y:auto;margin-right:6vw;opacity:0;transform:translateX(48px)}",
      ".sp--full.is-in .sp-panel{opacity:0;transform:translateX(48px)}",
      ".sp--full.is-drawer .sp-panel{opacity:1;transform:none;transition:opacity .45s ease,transform .7s cubic-bezier(.2,.8,.2,1)}",
      // desktop: a scene can put the card on the left (product pinned to the right edge)
      "@media (min-width:750px){",
      "dialog.sp--full.sp--card-left{justify-content:flex-start}",
      ".sp--card-left .sp-panel{margin-right:0;margin-left:6vw}",
      ".sp--card-left .sp-layers{left:calc(" + t.cardWidth + " + 8vw);right:0}",
      ".sp-layer--right{left:auto!important;right:0;translate:none}",
      ".sp--full.is-drawer.sp--swap .sp-panel{opacity:0;transition:opacity .2s ease}",
      "}",
      ".sp--full .sp-body{padding:48px 40px 40px}",
      ".sp--full .sp-close{background:rgba(0,0,0,.06)}",
      "@media (max-width:749px){",
      "dialog.sp--full{align-items:flex-end;justify-content:center}",
      // Before the drawer: products sit lower and larger in the full screen. When the drawer
      // rises, the product area shrinks to the top band and the products glide up with it.
      ".sp-layers{right:0;bottom:auto;height:" + t.mobileIntroHeight + ";transform:scale(1.1)}",
      ".is-drawer .sp-layers{height:" + t.mobileStageHeight + ";transform:none}",
      ".sp-layer{left:var(--mx,var(--x));bottom:var(--mb,var(--b));height:var(--mh,var(--h))}",
      ".sp-layer--top{bottom:auto;top:var(--mt,var(--t))}",
      // Fixed split (scene on top, card below) so the product always sits right above the card.
      ".sp--full .sp-panel,.sp--full.is-in .sp-panel{width:100%;margin:0;height:auto;max-height:80%;border-radius:var(--radius) var(--radius) 0 0;opacity:1;transform:translateY(105%)}",
      ".sp--full.is-drawer .sp-panel{transform:none;transition:transform .75s cubic-bezier(.2,.8,.2,1)}",
      ".sp--full .sp-body{padding:26px 20px calc(22px + env(safe-area-inset-bottom))}",
      ".sp--full .sp-opt{padding:11px 14px}",
      ".sp--nointro .sp-layers,.sp--nointro.is-drawer .sp-layers{height:var(--band," + t.mobileStageHeight + ");transform:none;transition:height .5s cubic-bezier(.2,.8,.2,1)}",
      ".sp--nointro .sp-layer{transition-property:transform,opacity,height,bottom,left}",
      // the hand stays down until the card is fully in, then rises from behind its top edge
      ".sp--nointro.is-in:not(.is-drawer) .sp-scene--rise.is-active .sp-layer{transform:translateY(105%)}",
      ".sp--nointro.is-drawer .sp-scene--rise.is-active .sp-layer{transition-delay:calc(var(--d,0s) + .45s)}",
      ".sp--full .sp-body{padding:22px 20px calc(18px + env(safe-area-inset-bottom))}",
      ".sp--full .sp-title{margin-bottom:6px}",
      ".sp--full .sp-text{margin-bottom:14px}",
      ".sp--full.sp--nointro .sp-panel,.sp--full.sp--nointro.is-in .sp-panel{transform:none;opacity:0}",
      ".sp--full.sp--nointro.is-drawer .sp-panel{opacity:1;transition:opacity .45s ease}",
      ".sp-drift{--ts:var(--mts,var(--ts0))}",
      ".sp-photo{-webkit-mask-image:linear-gradient(180deg,#000 82%,transparent);mask-image:linear-gradient(180deg,#000 82%,transparent);object-position:var(--mpos,var(--pos,50% 50%))}",
      "}",
      "@media (prefers-reduced-motion:reduce){.sp-panel,.sp-media img,.sp-teaser,dialog.sp::backdrop,.sp-stage,.sp-scene,.sp-bg-img,.sp-layer,.is-in .sp-scene.is-active .sp-layer{transition:none}.sp-step.is-active,.sp-layer img,.sp-drift{animation:none}.is-active .sp-check svg{animation:none;stroke-dashoffset:0}.sp-layer{transform:none}}",
    ].join("\n");
  }

  var ICON_X = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13"/></svg>';
  var ICON_CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

  // ---------- defaults ----------
  var DEFAULTS = {
    enabled: true,
    formId: "signup-popup",
    formName: "Signup popup",
    klaviyo: { companyId: "", listId: "", customSource: "Signup popup", revision: DEFAULT_REVISION, dryRun: false },
    offer: { code: "", applyUrl: null },
    flow: ["quiz", "email", "sms", "success"],
    theme: {
      accent: "#222", accentText: "#fff", ink: "#1a1a1a", muted: "#5c5c5c", paper: "#fff", line: "#d9d4ca",
      radius: "16px", backdrop: "rgba(20,18,14,.55)", fontHeading: "Georgia,serif",
      fontBody: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
      fontStylesheet: null, mobileImageHeight: "150px", cardWidth: "440px", mobileStageHeight: "40vh", mobileIntroHeight: "78vh",
    },
    image: { src: null, mobileSrc: null, alt: "", position: "50% 50%" },
    layout: "modal",
    drawerDelayMs: { desktop: 650, mobile: 1300 },
    scenes: {},
    steps: {},
    teaser: { enabled: true, label: "" },
    rules: {
      delayMs: { desktop: 6000, mobile: 10000 },
      scrollPercent: 40,
      minDelayMs: 3000,
      exitIntent: true,
      reshowAfterDismissDays: 3,
      maxAutoShows: 3,
      excludePaths: [],
      suppressIfCustomer: false,
      suppressUtmSources: [],
      deferWhenBodyClass: ["overflow-hidden"],
    },
    track: { klaviyoEvent: null, endpoint: null, ga4: null },
  };

  // ---------- engine ----------
  function Popup(config, context) {
    config = config || {};
    var want = new URLSearchParams(location.search).get("spv") || (context && context.variant) || config.variant;
    if (want && config.variants && config.variants[want]) {
      var v = config.variants[want];
      config = merge(config, v);
      if (v.scenes) config.scenes = v.scenes;       // a variant's scenes replace the base set
    }
    this.cfg = merge(DEFAULTS, config);
    this.variant = want && config.variants && config.variants[want] ? want : "base";
    this.ctx = context || {};
    this.key = "signup-popup:" + this.cfg.formId;
    this.answers = {};
    this.email = null;
    this.stepIndex = -1;
    this.isOpen = false;
    this.built = false;
    this.opener = null;
  }

  Popup.prototype.state = function () { return store.get(this.key) || {}; };
  Popup.prototype.saveState = function (patch) { store.set(this.key, merge(this.state(), patch)); };

  Popup.prototype.emit = function (name, data) {
    var detail = merge({ event: name, formId: this.cfg.formId, variant: this.variant, path: location.pathname, t: Date.now() }, data || {});
    try { window.dispatchEvent(new CustomEvent("signup-popup", { detail: detail })); } catch (e) {}
    var tr = this.cfg.track;
    // ga4 = a measurement ID. send_to keeps these events out of any Google Ads
    // tags sharing the page's gtag, which would otherwise receive every event.
    if (typeof tr.ga4 === "string" && tr.ga4 && typeof window.gtag === "function") {
      try { window.gtag("event", "signup_popup_" + name, { send_to: tr.ga4, form_id: this.cfg.formId, step: detail.step, value_key: detail.value }); } catch (e) {}
    }
    if (tr.endpoint && navigator.sendBeacon) {
      try { navigator.sendBeacon(tr.endpoint, JSON.stringify(detail)); } catch (e) {}
    }
    if (this.cfg.klaviyo.dryRun) (window.__signupPopupLog = window.__signupPopupLog || []).push({ type: "event", detail: detail });
  };

  Popup.prototype.pathExcluded = function () {
    var p = location.pathname;
    return (this.cfg.rules.excludePaths || []).some(function (x) { return p === x || p.indexOf(x.replace(/\/?$/, "/")) === 0 || p.indexOf(x) === 0; });
  };

  // Returns "auto" (may auto-open), "teaser" (teaser only) or "none".
  Popup.prototype.eligibility = function () {
    var r = this.cfg.rules;
    this.why = "";
    if (!this.cfg.enabled) { this.why = "popup is switched off"; return "none"; }
    if (this.pathExcluded()) { this.why = "never shown on this page"; return "none"; }
    var st = this.state();
    if (st.status === "subscribed") { this.why = "this browser already signed up"; return "none"; }
    var qs = new URLSearchParams(location.search);
    var utm = (qs.get("utm_source") || "").toLowerCase();
    if (qs.has("_kx") || (utm && (r.suppressUtmSources || []).indexOf(utm) !== -1)) {
      // Arrived from one of the merchant's own emails: already on the list.
      this.saveState({ status: "subscribed", via: "email-click", at: Date.now() });
      this.why = "arrived from a Klaviyo email"; return "none";
    }
    if (r.suppressIfCustomer && this.ctx.customerLoggedIn) { this.why = "you're logged in to a customer account"; return "none"; }
    if (store.sget(this.key + ":shown")) { this.why = "already auto-opened this session (tab)"; return "teaser"; }
    if ((st.autoShows || 0) >= r.maxAutoShows) { this.why = "auto-open limit reached"; return "teaser"; }
    if (st.dismissedAt && Date.now() - st.dismissedAt < r.reshowAfterDismissDays * DAY) { this.why = "closed recently (waits " + r.reshowAfterDismissDays + " days)"; return "teaser"; }
    return "auto";
  };

  Popup.prototype.init = function () {
    var self = this;
    var qs = new URLSearchParams(location.search);
    if (qs.get("sp") === "reset") { store.del(this.key); try { sessionStorage.removeItem(this.key + ":shown"); } catch (e) {} }
    var force = qs.get("sp") === "open";
    var el = force ? "auto" : this.eligibility();
    if (qs.has("sp")) {
      var mobile = window.matchMedia("(max-width: 749px)").matches, r0 = this.cfg.rules;
      var msg = force ? "Test mode: opening now"
        : el === "auto" ? "Popup ready: opens in " + Math.round((mobile ? r0.delayMs.mobile : r0.delayMs.desktop) / 1000) + "s or when you scroll"
        : "Popup won't auto-open: " + this.why + (qs.get("sp") !== "reset" ? " (use ?sp=reset)" : "");
      if (window.console) console.info("[signup-popup] " + msg);
    }
    if (el === "none") return;

    this.mountHost();
    if (this.cfg.theme.fontStylesheet && !document.querySelector('link[data-signup-popup-font]')) {
      var l = document.createElement("link");
      l.rel = "stylesheet"; l.href = this.cfg.theme.fontStylesheet; l.setAttribute("data-signup-popup-font", "");
      document.head.appendChild(l);
    }
    if (force) { this.open("forced"); return; }
    if (el === "teaser") { this.showTeaser(); return; }
    this.schedule();
  };

  Popup.prototype.schedule = function () {
    var self = this, r = this.cfg.rules;
    var mobile = window.matchMedia("(max-width: 749px)").matches;
    var start = Date.now();
    var fired = false;
    function fire(reason) {
      if (fired || self.isOpen) return;
      if (Date.now() - start < r.minDelayMs) return;
      fired = true;
      cleanup();
      self.openWhenClear(reason);
    }
    var timer = setTimeout(function () { fire("delay"); }, mobile ? r.delayMs.mobile : r.delayMs.desktop);
    var minTimer = setTimeout(function () { onScroll(); }, r.minDelayMs + 50);
    function onScroll() {
      if (r.scrollPercent == null) return;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      if (h > 0 && (window.scrollY / h) * 100 >= r.scrollPercent) fire("scroll");
    }
    function onLeave(e) { if (!e.relatedTarget && e.clientY <= 0) fire("exit"); }
    window.addEventListener("scroll", onScroll, { passive: true });
    if (r.exitIntent && !mobile) document.addEventListener("mouseout", onLeave);
    // Download and decode the opening scene while the visitor browses, after the page itself
    // has loaded, so the popup never opens onto half-drawn images.
    var warm = function () { setTimeout(function () { self.preloadFirst(); }, 800); };
    if (document.readyState === "complete") warm(); else window.addEventListener("load", warm, { once: true });
    function cleanup() {
      clearTimeout(timer); clearTimeout(minTimer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseout", onLeave);
    }
  };

  // Never open over a cart drawer, box builder or another dialog. Wait for it to close.
  Popup.prototype.openWhenClear = function (reason) {
    var self = this, tries = 0;
    (function attempt() {
      var cls = self.cfg.rules.deferWhenBodyClass || [];
      var busy = cls.some(function (c) { return document.body.classList.contains(c); }) ||
        !!document.querySelector("dialog[open]") || document.visibilityState === "hidden";
      if (!busy) return self.open(reason);
      if (++tries < 90) setTimeout(attempt, 2000);
    })();
  };

  // ---------- image readiness ----------
  // Every image a scene needs (the background for this device plus each product layer).
  Popup.prototype.sceneUrls = function (id) {
    var self = this;
    if (!this.isFull()) { var one = this.imageSrc(); return one ? [one] : []; }
    var sc = this.cfg.scenes[id] || {};
    return [this.isMobile() && sc.bgMobile ? sc.bgMobile : sc.bg, sc.photo]
      .concat((sc.drift || []).map(function (t) { return t.src; }))
      .concat((sc.layers || []).map(function (l) { return l.src; }))
      .filter(Boolean).map(function (u) { return self.src(u); });
  };
  // Resolves once every image of the scene is downloaded AND decoded. Cached per scene.
  // A failed image resolves too (its layer is simply left out) so one bad URL can't block the popup.
  Popup.prototype.preload = function (id) {
    var self = this;
    this._ready = this._ready || {};
    this._imgs = this._imgs || {};
    if (this._ready[id]) return this._ready[id];
    this._ready[id] = Promise.all(this.sceneUrls(id).map(function (url) {
      if (self._imgs[url]) return self._imgs[url].p;
      var img = new Image();
      img.decoding = "async";
      var p = new Promise(function (res) {
        img.onload = function () { (img.decode ? img.decode() : Promise.resolve()).then(res, res); };
        img.onerror = function () { img.failed = true; res(); };
      });
      img.src = url;
      self._imgs[url] = { img: img, p: p };
      return p;
    }));
    return this._ready[id];
  };
  Popup.prototype.firstScene = function () { return this.isFull() ? this.sceneFor(this.stepsInFlow()[0]) : "_single"; };
  // Opening scene first; the other scenes follow in the background so step changes are instant.
  Popup.prototype.preloadFirst = function () {
    var self = this;
    return this.preload(this.firstScene()).then(function () {
      if (self.isFull()) Object.keys(self.cfg.scenes).forEach(function (id) { self.preload(id); });
    });
  };
  // true if the opening scene is ready within ms.
  Popup.prototype.whenReady = function (ms) {
    var done = this.preloadFirst().then(function () { return true; });
    var timeout = new Promise(function (res) { setTimeout(function () { res(false); }, ms); });
    return Promise.race([done, timeout]);
  };
  Popup.prototype.imageSrc = function () {
    var im = this.cfg.image;
    if (!im.src) return null;
    return window.matchMedia("(max-width: 749px)").matches && im.mobileSrc ? im.mobileSrc : im.src;
  };

  Popup.prototype.mountHost = function () {
    if (this.host) return;
    // A custom element, not a <div>: the host's content lives in the shadow root, so
    // the light DOM is empty and themes' `div:empty{display:none}` (Dawn) would hide it.
    this.host = document.createElement("signup-popup-root");
    this.host.setAttribute("data-signup-popup-host", this.cfg.formId);
    this.host.style.cssText = "display:block!important;position:static!important;width:0;height:0;margin:0;padding:0;border:0";
    this.root = this.host.attachShadow ? this.host.attachShadow({ mode: "open" }) : this.host;
    var style = document.createElement("style");
    style.textContent = css(this.cfg.theme);
    this.root.appendChild(style);
    this.wrap = document.createElement("div");
    this.wrap.className = "sp";
    this.root.appendChild(this.wrap);
    document.body.appendChild(this.host);
  };

  Popup.prototype.stepsInFlow = function () {
    var s = this.cfg.steps;
    return this.cfg.flow.filter(function (name) { return s[name] && s[name].enabled !== false; });
  };

  Popup.prototype.isFull = function () {
    return this.cfg.layout === "fullscreen" && Object.keys(this.cfg.scenes || {}).length > 0;
  };
  Popup.prototype.isMobile = function () { return window.matchMedia("(max-width: 749px)").matches; };
  // "asset:name.png" resolves against the theme's asset folder (theme mode) or config.assetBase (hosted).
  Popup.prototype.src = function (u) {
    if (!u) return "";
    if (u.indexOf("asset:") === 0) return (this.ctx.assetBase || this.cfg.assetBase || "") + u.slice(6);
    return u;
  };
  Popup.prototype.stageHtml = function () {
    var self = this, mobile = this.isMobile(), out = ['<div class="sp-stage" aria-hidden="true">'];
    Object.keys(this.cfg.scenes).forEach(function (id) {
      var sc = self.cfg.scenes[id];
      var bg = self.src(mobile && sc.bgMobile ? sc.bgMobile : sc.bg);
      out.push('<div class="sp-scene' + (sc.style ? " sp-scene--" + esc(sc.style) : "") + '" data-scene="' + esc(id) + '">');
      if (bg) out.push('<img class="sp-bg-img" alt="" data-src="' + esc(bg) + '">');
      (sc.drift || []).forEach(function (t) {
        out.push('<div class="sp-drift" style="' + esc("background-image:url(" + self.src(t.src) + ");--ts0:" + t.size + (t.mobileSize ? ";--mts:" + t.mobileSize : "") + ";--dur:" + (t.duration || 60) + "s") + '"></div>');
      });
      out.push('<div class="sp-layers">');
      if (sc.photo) out.push('<img class="sp-photo" alt="" style="--pos:' + esc(sc.photoPosition || "50% 50%") + (sc.photoPositionMobile ? ";--mpos:" + esc(sc.photoPositionMobile) : "") + '" data-src="' + esc(self.src(sc.photo)) + '">');
      (sc.layers || []).forEach(function (l, i) {
        var m = l.mobile || {};
        var vars = "--x:" + l.x + ";--b:" + l.b + ";--h:" + l.h + ";--r:" + (l.r || "0deg") + ";--z:" + (l.z || 1) +
          ";--d:" + (l.delay != null ? l.delay : (sc.style === "flat" ? 0.1 + i * 0.06 : 0.12 + i * 0.14)) + "s;--f:" + (-i * 2.3) + "s" +
          (l.anchor === "center" || sc.anchor === "center" ? ";--tx:-50%;--ty:50%" : "") +
          (l.anchor === "bottom-center" ? ";--tx:-50%;--ty:0" : "") +
          // enter:"left" slides the piece in from off the left edge (e.g. a hand reaching in)
          (l.enter === "left" ? ";--ex:-110%;--ey:0px" : "") +
          (l.from ? ";--fx:" + l.from[0] + ";--fy:" + l.from[1] : "") +
          (m.x ? ";--mx:" + m.x : "") + (m.b ? ";--mb:" + m.b : "") + (m.h ? ";--mh:" + m.h : "");
        if (l.t != null) vars += ";--t:" + l.t + (m.t != null ? ";--mt:" + m.t : "");
        out.push('<span class="sp-layer' + (l.t != null ? " sp-layer--top" : "") + (l.pin === "right" ? " sp-layer--right" : "") + '" style="' + esc(vars) + '"><img alt="" data-src="' + esc(self.src(l.src)) + '"></span>');
      });
      out.push("</div></div>");
    });
    out.push("</div>");
    return out.join("");
  };
  // Put a scene's preloaded, already-decoded images into the page (swapping out the
  // placeholders), so nothing paints progressively once the scene is shown.
  Popup.prototype.loadScene = function (id) {
    if (!this.wrap) return;
    var cache = this._imgs || {};
    this.wrap.querySelectorAll('.sp-scene[data-scene="' + id + '"] img[data-src]').forEach(function (ph) {
      var url = ph.getAttribute("data-src"), hit = cache[url];
      if (hit && hit.img.failed) { var layer = ph.closest(".sp-layer"); (layer || ph).remove(); return; }
      if (hit && hit.img.complete) {
        // The same picture can appear in several scenes: the first scene takes the decoded
        // element, later ones get a copy (already in the memory cache, so it paints at once).
        var el = hit.used ? hit.img.cloneNode() : hit.img;
        hit.used = true;
        el.className = ph.className; el.alt = ph.alt || "";
        ph.replaceWith(el);
      } else { ph.src = url; ph.removeAttribute("data-src"); }
    });
  };
  // Switch scenes only once the new scene is fully decoded; the old one holds until then.
  Popup.prototype.showScene = function (id) {
    if (!this.isFull() || !id) return;
    var sc = this.cfg.scenes[id];
    if (!sc) return;
    var self = this;
    this._wantScene = id;
    this.preload(id).then(function () {
      if (self._wantScene !== id) return;
      self.loadScene(id);
      // Let the freshly inserted images render once in their resting (inactive) state, so the
      // background settle and the rise actually animate when the scene becomes active.
      void self.wrap.offsetWidth;
      requestAnimationFrame(function () {
        if (self._wantScene !== id) return;
        self.wrap.querySelectorAll(".sp-scene").forEach(function (el) { el.classList.toggle("is-active", el.getAttribute("data-scene") === id); });
      });
      var stage = self.wrap.querySelector(".sp-stage");
      if (stage && sc.color) stage.style.setProperty("--scene", sc.color);
      // the card's coloured parts follow the scene's flavour colour
      if (sc.accent || sc.color) self.dialog.style.setProperty("--accent", sc.accent || sc.color);
      // card side (desktop): fade the card out, switch sides while it's invisible, fade back
      var left = sc.cardSide === "left", d = self.dialog;
      if (left !== d.classList.contains("sp--card-left")) {
        if (!d.classList.contains("is-drawer")) d.classList.toggle("sp--card-left", left);
        else {
          d.classList.add("sp--swap");
          self._swapping = true;
          setTimeout(function () {
            d.classList.toggle("sp--card-left", left);
            requestAnimationFrame(function () {
              d.classList.remove("sp--swap"); self._swapping = false;
              // focus only once the card is in its new place, or the browser's autofill
              // suggestions stay anchored where the field used to be
              if (self._focusPending) { self._focusPending = false; self.focusStep(); }
            });
          }, 210);
        }
      }
    });
  };
  // Phones: the card is as tall as its content, so the product band is the space above it
  // (plus the 24px the card's rounded top overlaps). Exposed as --band for the layers.
  Popup.prototype.fitBand = function () {
    if (!this.dialog || !this.isFull() || !this.isMobile()) return;
    var panel = this.dialog.querySelector(".sp-panel");
    var band = Math.max(160, this.dialog.clientHeight - panel.offsetHeight + 24);
    this.dialog.style.setProperty("--band", band + "px");
  };
  Popup.prototype.sceneFor = function (step) {
    var st = this.cfg.steps[step] || {};
    return st.scene || Object.keys(this.cfg.scenes || {})[0];
  };

  Popup.prototype.build = function () {
    if (this.built) return;
    this.built = true;
    var self = this, c = this.cfg, s = c.steps;
    var vars = { code: esc(c.offer.code) };
    var html = [];
    var full = this.isFull();
    html.push('<dialog class="sp' + (full ? " sp--full" : "") + (full && c.mobileIntro === false && this.isMobile() ? " sp--nointro" : "") + '" aria-modal="true">');
    if (full) html.push(this.stageHtml());
    html.push('<div class="sp-panel" part="panel">');
    html.push('<button type="button" class="sp-close" data-act="close" aria-label="Close">' + ICON_X + "</button>");
    if (!full) html.push('<div class="sp-media">' + (c.image.src ? '<img alt="' + esc(c.image.alt) + '" style="object-position:' + esc(c.image.position) + '">' : "") + "</div>");
    html.push('<div class="sp-body">');
    this.stepsInFlow().forEach(function (name) {
      var st = s[name];
      var id = "sp-t-" + name;
      html.push('<section class="sp-step" data-step="' + name + '" aria-labelledby="' + id + '">');
      if (st.eyebrow) html.push('<p class="sp-eyebrow">' + fill(esc(st.eyebrow), vars) + "</p>");
      if (name === "success") html.push('<div class="sp-check">' + ICON_CHECK + "</div>");
      html.push('<h2 class="sp-title" id="' + id + '">' + fill(esc(st.title), vars) + "</h2>");
      if (st.text) html.push('<p class="sp-text">' + fill(esc(st.text), vars) + "</p>");
      if (name === "quiz") {
        html.push('<div class="sp-options" role="group" aria-labelledby="' + id + '">');
        (st.options || []).forEach(function (o) {
          html.push('<button type="button" class="sp-opt" data-act="answer" data-value="' + esc(o.value) + '"><span class="sp-opt-dot"></span>' + esc(o.label) + "</button>");
        });
        html.push("</div>");
        if (st.skip) html.push('<button type="button" class="sp-link" data-act="skip">' + esc(st.skip) + "</button>");
      } else if (name === "email") {
        html.push('<form class="sp-form" data-form="email" novalidate>');
        // Honeypot. The name must not look like a real field ("company", "name"...): iOS and
        // Chrome autofill those despite autocomplete=off, which would flag real people as bots.
        html.push('<input class="sp-hp" name="sp_hp_confirm" tabindex="-1" autocomplete="new-password" aria-hidden="true">');
        html.push('<input class="sp-input" type="email" name="email" inputmode="email" autocomplete="email" required placeholder="' + esc(st.placeholder || "Email address") + '" aria-label="' + esc(st.placeholder || "Email address") + '">');
        html.push('<p class="sp-error" role="alert"></p>');
        html.push('<button class="sp-btn" type="submit">' + fill(esc(st.button || "Continue"), vars) + "</button>");
        html.push("</form>");
        if (st.fineprint) html.push('<p class="sp-fine">' + richText(st.fineprint) + "</p>");
      } else if (name === "sms") {
        html.push('<form class="sp-form" data-form="sms" novalidate>');
        html.push('<input class="sp-input" type="tel" name="phone" inputmode="tel" autocomplete="tel" required placeholder="' + esc(st.placeholder || "Phone number") + '" aria-label="' + esc(st.placeholder || "Phone number") + '">');
        html.push('<p class="sp-error" role="alert"></p>');
        // SMS consent sits ABOVE the button it describes (carrier / TCPA drafting practice).
        if (st.consent) html.push('<p class="sp-fine sp-consent">' + richText(st.consent) + "</p>");
        html.push('<button class="sp-btn" type="submit">' + esc(st.button || "Sign me up") + "</button>");
        html.push("</form>");
        if (st.skip) html.push('<button type="button" class="sp-link" data-act="sms-skip">' + esc(st.skip) + "</button>");
      } else if (name === "success") {
        if (c.offer.code) {
          html.push('<div class="sp-code"><span class="sp-code-val">' + esc(c.offer.code) + '</span><button type="button" class="sp-copy" data-act="copy">' + esc(st.copyLabel || "Copy") + "</button></div>");
        }
        if (c.offer.applyUrl && st.applyLabel) html.push('<button type="button" class="sp-btn" data-act="apply">' + esc(st.applyLabel) + "</button>");
        else html.push('<button type="button" class="sp-btn" data-act="close">' + esc(st.closeLabel || "Keep shopping") + "</button>");
        if (st.badges && st.badges.length) {
          html.push('<div class="sp-badges">' + (st.badgesTitle ? '<p class="sp-badges-h">' + esc(st.badgesTitle) + "</p>" : ""));
          st.badges.forEach(function (b) {
            html.push('<div class="sp-badge"><span class="sp-badge-i">' + esc(b.tag) + '</span><p class="sp-badge-t"><b>' + esc(b.title) + "</b> " + esc(b.text) + "</p></div>");
          });
          html.push("</div>");
        }
        if (st.fineprint) html.push('<p class="sp-fine">' + richText(st.fineprint) + "</p>");
      }
      html.push("</section>");
    });
    html.push("</div></div></dialog>");
    if (c.teaser.enabled && c.teaser.label) {
      html.push('<button type="button" class="sp-teaser" data-act="teaser" aria-label="' + esc(c.teaser.label) + '"><span>' + esc(c.teaser.label) +
        '</span><span class="sp-teaser-x" data-act="teaser-hide" role="button" aria-label="Hide">' + ICON_X + "</span></button>");
    }
    this.wrap.innerHTML = html.join("");
    this.dialog = this.wrap.querySelector("dialog");
    this.teaser = this.wrap.querySelector(".sp-teaser");

    var img = this.wrap.querySelector(".sp-media img");
    if (img) {
      img.addEventListener("load", function () { img.classList.add("is-loaded"); });
      img.src = this.imageSrc();
      if (img.complete) img.classList.add("is-loaded");
    }

    this.wrap.addEventListener("click", function (e) {
      var t = e.target.closest ? e.target.closest("[data-act]") : null;
      if (!t) {
        if (e.target === self.dialog) self.close("backdrop");
        return;
      }
      var act = t.getAttribute("data-act");
      if (act === "close") self.close("button");
      else if (act === "answer") self.answer(t);
      else if (act === "skip") { self.emit("quiz_skip"); self.next(); }
      else if (act === "sms-skip") { self.emit("sms_skip"); self.next(); }
      else if (act === "copy") self.copyCode(t);
      else if (act === "apply") self.apply();
      else if (act === "teaser-hide") { e.stopPropagation(); self.hideTeaser(true); }
      else if (act === "teaser") self.open("teaser");
    });
    this.dialog.addEventListener("cancel", function (e) { e.preventDefault(); self.close("escape"); });
    this.wrap.querySelectorAll("form").forEach(function (f) {
      f.addEventListener("submit", function (e) { e.preventDefault(); f.getAttribute("data-form") === "email" ? self.submitEmail(f) : self.submitSms(f); });
    });
  };

  Popup.prototype.showTeaser = function () {
    if (!this.cfg.teaser.enabled || !this.cfg.teaser.label || store.sget(this.key + ":teaser-hidden")) return;
    if (this.state().status === "subscribed") return;
    this.build();
    var t = this.teaser;
    if (!t) return;
    requestAnimationFrame(function () { requestAnimationFrame(function () { t.classList.add("is-shown"); }); });
  };
  Popup.prototype.hideTeaser = function (persist) {
    if (this.teaser) this.teaser.classList.remove("is-shown");
    if (persist) store.sset(this.key + ":teaser-hidden", "1");
  };

  // Never open onto half-loaded images. Automatic opens wait for the opening scene and are
  // skipped (not counted as shown) if it isn't ready in time; opens the visitor asked for
  // (teaser tap, ?sp=open) wait a shorter while and then go ahead.
  Popup.prototype.open = function (reason) {
    if (this.isOpen || this._opening) return;
    var self = this, asked = reason === "teaser" || reason === "forced" || reason === "api";
    this._opening = true;
    this.whenReady(asked ? 5000 : 12000).then(function (ok) {
      self._opening = false;
      if (!ok && !asked) { self.emit("skip_not_ready", { reason: reason }); return; }
      self.openNow(reason);
    });
  };

  Popup.prototype.openNow = function (reason) {
    if (this.isOpen) return;
    this.build();
    this.isOpen = true;
    this.opener = document.activeElement;
    this.hideTeaser(false);
    if (reason !== "teaser" && reason !== "forced") {
      store.sset(this.key + ":shown", "1");
      this.saveState({ autoShows: (this.state().autoShows || 0) + 1 });
    }
    if (this.stepIndex < 0 || (this.currentStep() === "success" && this.state().status !== "subscribed")) this.go(0);
    this._prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    // showModal() focuses the first focusable element (the close button in the card). With
    // the card still off-screen that scrolls the whole dialog and everything jumps. Focus the
    // dialog itself instead, and undo any scroll the browser did anyway.
    this.dialog.setAttribute("tabindex", "-1");
    this.dialog.setAttribute("autofocus", "");
    if (this.dialog.showModal) this.dialog.showModal(); else this.dialog.setAttribute("open", "");
    try { this.dialog.focus({ preventScroll: true }); } catch (e) {}
    this.dialog.scrollTop = 0; this.dialog.scrollLeft = 0;
    this.fitBand();
    if (!this._fitBound) { var me = this; this._fitBound = true; window.addEventListener("resize", function () { if (me.isOpen) me.fitBand(); }); }
    var d = this.dialog, dd = this.cfg.drawerDelayMs;
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var drawerAt = !this.isFull() || reduce ? 0 : (this.isMobile() ? (this.cfg.mobileIntro === false ? 120 : dd.mobile) : dd.desktop);
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      d.classList.add("is-in");
      setTimeout(function () { d.classList.add("is-drawer"); }, drawerAt);
    }); });
    var self2 = this;
    setTimeout(function () { self2.focusStep(); }, drawerAt);
    this.emit("open", { reason: reason, step: this.currentStep() });
  };

  Popup.prototype.close = function (how) {
    if (!this.isOpen) return;
    // Closing after the email step (e.g. on the SMS ask) must not lose the code they earned.
    var succ = this.stepsInFlow().indexOf("success");
    // Only an email given in THIS visit earns the code; a stored "subscribed" from an earlier
    // visit must not turn the close button into a shortcut to the code.
    if (how !== "done" && succ > this.stepIndex && this.email) {
      if (this.currentStep() === "sms") this.emit("sms_skip", { how: how });
      return this.go(succ);
    }
    var self = this, d = this.dialog;
    this.isOpen = false;
    var step = this.currentStep();
    d.classList.remove("is-in", "is-drawer");
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setTimeout(function () {
      if (d.close) d.close(); else d.removeAttribute("open");
      document.documentElement.style.overflow = self._prevOverflow || "";
      if (self.opener && self.opener.focus) try { self.opener.focus(); } catch (e) {}
    }, reduce ? 0 : 320);
    this.emit("close", { how: how, step: step });
    if (this.state().status !== "subscribed") {
      this.saveState({ dismissedAt: Date.now() });
      setTimeout(function () { self.showTeaser(); }, reduce ? 0 : 700);
    }
  };

  Popup.prototype.currentStep = function () { return this.stepsInFlow()[this.stepIndex] || null; };

  Popup.prototype.go = function (i) {
    var steps = this.stepsInFlow();
    if (i >= steps.length) return this.close("done");
    this.stepIndex = i;
    var name = steps[i];
    this.wrap.querySelectorAll(".sp-step").forEach(function (el) {
      el.classList.toggle("is-active", el.getAttribute("data-step") === name);
    });
    this.dialog.setAttribute("aria-labelledby", "sp-t-" + name);
    this.fitBand();
    // if this step moves the card to the other side, hold focus until it has moved
    var nsc = this.isFull() && this.cfg.scenes[this.sceneFor(name)];
    if (nsc && this.dialog.classList.contains("is-drawer") && ((nsc.cardSide === "left") !== this.dialog.classList.contains("sp--card-left"))) this._swapping = true;
    this.showScene(this.sceneFor(name));
    if (this.isOpen) { this.focusStep(); this.emit("step_view", { step: name }); }
  };
  Popup.prototype.next = function () { this.go(this.stepIndex + 1); };

  Popup.prototype.focusStep = function () {
    if (this._swapping) { this._focusPending = true; return; }
    var step = this.wrap.querySelector(".sp-step.is-active");
    if (!step) return;
    // Inputs get focus (desktop only, below). Otherwise the heading does, so no option
    // looks pre-selected and screen readers start at the question.
    var target = step.querySelector("input:not(.sp-hp)") || step.querySelector("h2");
    // On phones, focusing an input pops the keyboard over the offer before it is read.
    if (target && target.tagName === "INPUT" && window.matchMedia("(max-width: 749px)").matches) target = step.querySelector("h2");
    if (target) { if (target.tagName === "H2") target.setAttribute("tabindex", "-1"); setTimeout(function () { try { target.focus({ preventScroll: true }); } catch (e) {} }, 60); }
    this._stepShownAt = Date.now();
  };

  Popup.prototype.answer = function (btn) {
    var q = this.cfg.steps.quiz;
    var value = btn.getAttribute("data-value");
    this.answers[q.property] = value;
    btn.parentNode.querySelectorAll(".sp-opt").forEach(function (b) { b.classList.toggle("is-picked", b === btn); });
    this.emit("quiz_answer", { value: value });
    var self = this;
    setTimeout(function () { self.next(); }, 260);
  };

  Popup.prototype.profileProps = function () {
    var p = {};
    for (var k in this.answers) p[k] = this.answers[k];
    p.signup_form_id = this.cfg.formId;
    p.signup_form_name = this.cfg.formName;
    p.signup_method = "popup";
    if (this.variant !== "base") p.signup_variant = this.variant;
    p.signup_page = location.pathname;
    return p;
  };

  // POST /client/subscriptions. Resolves {ok, status}. Never throws.
  Popup.prototype.subscribe = function (attrs) {
    var k = this.cfg.klaviyo;
    var body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: k.customSource,
          profile: { data: { type: "profile", attributes: attrs } },
        },
        relationships: { list: { data: { type: "list", id: k.listId } } },
      },
    };
    return this.send("/client/subscriptions", body);
  };
  Popup.prototype.trackEvent = function (props) {
    var name = this.cfg.track.klaviyoEvent;
    if (!name || !this.email) return;
    this.send("/client/events", {
      data: {
        type: "event",
        attributes: {
          properties: props,
          metric: { data: { type: "metric", attributes: { name: name } } },
          profile: { data: { type: "profile", attributes: { email: this.email } } },
        },
      },
    });
  };
  Popup.prototype.send = function (path, body) {
    var k = this.cfg.klaviyo;
    if (k.dryRun) {
      (window.__signupPopupLog = window.__signupPopupLog || []).push({ type: "request", path: path, body: body });
      return new Promise(function (res) { setTimeout(function () { res({ ok: true, status: 202 }); }, 350); });
    }
    return fetch(KLAVIYO_BASE + path + "?company_id=" + encodeURIComponent(k.companyId), {
      method: "POST",
      headers: { "Content-Type": "application/vnd.api+json", accept: "application/vnd.api+json", revision: k.revision },
      body: JSON.stringify(body),
      keepalive: true,
    }).then(function (r) { return { ok: r.ok, status: r.status }; }, function () { return { ok: false, status: 0 }; });
  };

  Popup.prototype.busy = function (form, on) {
    var b = form.querySelector(".sp-btn");
    if (on) { b.setAttribute("data-label", b.textContent); b.textContent = "One sec…"; b.disabled = true; }
    else { b.textContent = b.getAttribute("data-label") || b.textContent; b.disabled = false; }
  };
  Popup.prototype.error = function (form, msg) { form.querySelector(".sp-error").textContent = msg || ""; };

  Popup.prototype.submitEmail = function (form) {
    var self = this;
    var email = form.email.value.trim().toLowerCase();
    this.error(form, "");
    // Bots fill the hidden field. They see a normal success screen, but nothing is sent and
    // nothing is remembered in this browser.
    if (form.sp_hp_confirm.value) { this.emit("bot_blocked"); this.email = null; return this.next(); }
    if (!validEmail(email)) return this.error(form, this.cfg.steps.email.invalid || "Please enter a valid email address.");
    this.busy(form, true);
    this.subscribe({
      email: email,
      properties: this.profileProps(),
      subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } },
    }).then(function (r) {
      self.busy(form, false);
      if (r.status === 400) { self.emit("error", { step: "email", status: 400 }); return self.error(form, self.cfg.steps.email.invalid || "Please enter a valid email address."); }
      // Network or server trouble: they gave us their email in good faith, so they still get the code.
      if (!r.ok) self.emit("error", { step: "email", status: r.status });
      self.finishEmail(email, r.ok);
    });
  };
  Popup.prototype.finishEmail = function (email, ok) {
    this.email = email;
    this.saveState({ status: "subscribed", at: Date.now() });
    this.emit("email_submit", { ok: ok, value: this.answers[(this.cfg.steps.quiz || {}).property] || null });
    if (ok) {
      this.trackEvent(merge(this.profileProps(), { code: this.cfg.offer.code || null }));
      // Tie this browser to the profile so browse and cart activity is attributed.
      try { if (window.klaviyo && typeof window.klaviyo.identify === "function") window.klaviyo.identify({ email: email }); } catch (e) {}
    }
    this.next();
  };

  Popup.prototype.submitSms = function (form) {
    var self = this, st = this.cfg.steps.sms;
    this.error(form, "");
    var phone = toE164(form.phone.value, st.countryCode || "1");
    if (!phone) return this.error(form, st.invalid || "Please enter a valid mobile number.");
    if (!this.email) return this.next();
    this.busy(form, true);
    this.subscribe({
      email: this.email,
      phone_number: phone,
      subscriptions: { sms: { marketing: { consent: "SUBSCRIBED" } } },
    }).then(function (r) {
      self.busy(form, false);
      if (r.status === 400) { self.emit("error", { step: "sms", status: 400 }); return self.error(form, st.invalid || "Please enter a valid mobile number."); }
      self.emit("sms_submit", { ok: r.ok });
      self.next();
    });
  };

  Popup.prototype.copyCode = function (btn) {
    var code = this.cfg.offer.code, st = this.cfg.steps.success;
    var done = function () { btn.textContent = st.copiedLabel || "Copied"; setTimeout(function () { btn.textContent = st.copyLabel || "Copy"; }, 1800); };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(code).then(done, done); else done();
    this.emit("code_copy");
  };
  Popup.prototype.apply = function () {
    var c = this.cfg;
    this.emit("apply_click");
    location.href = fill(c.offer.applyUrl, {
      code: encodeURIComponent(c.offer.code),
      path: encodeURIComponent(location.pathname + location.search),
    });
  };

  // ---------- bootstrap ----------
  var instance = null;
  function start(config, context) {
    if (instance) return instance;
    instance = new Popup(config, context);
    instance.init();
    return instance;
  }

  window.SignupPopup = {
    __loaded: true,
    version: VERSION,
    init: start,
    open: function () { if (instance) { instance.mountHost(); instance.open("api"); } },
    reset: function () { if (instance) store.del(instance.key); },
  };

  function boot() {
    var inline = document.querySelector('script[type="application/json"][data-signup-popup]');
    if (inline) {
      try {
        var data = JSON.parse(inline.textContent);
        return start(data.config, data.context);
      } catch (e) { if (window.console) console.warn("[signup-popup] bad inline config", e); return; }
    }
    var me = CURRENT_SCRIPT || document.querySelector("script[data-config-url]");
    var url = me && me.getAttribute("data-config-url");
    if (url) {
      fetch(url, { credentials: "omit" }).then(function (r) { return r.ok ? r.json() : null; }).then(function (data) {
        if (data) start(data.config || data, data.context || {});
      }).catch(function () {});
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
